import { THDF_QR_PREFIX } from "../lib/traveller/thdf/qr";
import ThdfQrLookup from "../components/ThdfQrLookup";
import React from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import DocumentScanner from "../components/scanners/DocumentScanner";

const QR_TYPE_META = {
  "traveller.clearanceCertificate": { subjectLabel: "Traveller", kind: "clearance" },
  "humanRemains.clearanceCertificate": { subjectLabel: "Deceased", kind: "clearance" },
  "samples.clearanceCertificate": { subjectLabel: "Sample", kind: "clearance" },
  detention_certificate: { subjectLabel: "Consignment", kind: "detention" },
  "cargo.inspection": { subjectLabel: "Inspection", kind: "cargo" },
  "cargo.certificate": { subjectLabel: "Cargo certificate", kind: "cargo" },
  "environmentalHealth.foodCondemnation": { subjectLabel: "Food item", kind: "environmentalHealth" },
  "traveller.primaryScreening": { subjectLabel: "Traveller", kind: "screening" },
  "traveller.thdfDeclaration": { subjectLabel: "Traveller", kind: "thdf" },
  "conveyance.sanitationCertificate": { subjectLabel: "Conveyance", kind: "sanitation" },
};

export const SUPPORTED_QR_TYPES = new Set(Object.keys(QR_TYPE_META));

export const CLEARANCE_HINTS = [
  "QR payloads must be valid JSON (e.g. scan the official Port Health printout, not a photo).",
  "Type should identify the document (clearanceCertificate, detention_certificate, cargo.inspection, etc.).",
  "Include issuer metadata and timestamps so the registry can verify authenticity.",
];

export const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString();
  }
  return value;
};

const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");

const parseNumber = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveIdentifier = (structured) => {
  return (
    normalizeString(
      structured.id ??
        structured.clearanceId ??
        structured.certificateNumber ??
        structured.reference ??
        structured.consignmentId ??
        structured.thdfId ??
        structured.travellerId ??
        structured.outcomeId ??
        "",
    ) || null
  );
};

const parseClearanceLike = (structured, payloadType) => {
  const certificateNumber = normalizeString(
    structured.certificateNumber ?? structured.reference ?? structured.clearanceId ?? structured.id ?? "",
  );
  const clearanceId = normalizeString(structured.clearanceId ?? structured.id ?? certificateNumber);
  const travellerName = normalizeString(
    structured.travellerName ?? structured.deceasedName ?? structured.name ?? "",
  );
  return {
    certificateNumber,
    clearanceId,
    subjectName: travellerName,
    subjectLabel: QR_TYPE_META[payloadType]?.subjectLabel ?? "Subject",
    travellerGender: normalizeString(structured.travellerGender ?? structured.gender ?? ""),
    passportNumber: normalizeString(structured.passportNumber ?? structured.passport ?? ""),
    nationality: normalizeString(structured.nationality ?? structured.country ?? ""),
    remarks: normalizeString(structured.remarks ?? structured.notes ?? structured.comments ?? ""),
    portOfEntry: normalizeString(
      structured.port ?? structured.portOfEntry ?? structured.portName ?? structured.pointOfEntry ?? "",
    ),
  };
};

const parseDetentionCertificate = (structured) => {
  const releasedAt = structured.releasedAt ?? structured.releasedDate;
  const status = releasedAt ? "Released" : normalizeString(structured.status ?? "Active hold");
  return {
    subjectName: normalizeString(structured.consignmentId ?? structured.id ?? ""),
    subjectLabel: "Consignment",
    status,
    remarks: normalizeString(structured.conditions ?? structured.reason ?? structured.notes ?? ""),
    extras: [
      { label: "Consignment ID", value: normalizeString(structured.consignmentId ?? "") },
      { label: "Issued at", value: structured.issuedAt },
      { label: "Released at", value: releasedAt },
    ],
  };
};

const parseCargoInspection = (structured) => {
  const consignments = Array.isArray(structured.consignments) ? structured.consignments.length : null;
  return {
    subjectName: normalizeString(structured.facilityName ?? structured.id ?? ""),
    subjectLabel: "Inspection",
    status: normalizeString(structured.status ?? structured.outcome ?? ""),
    remarks: normalizeString(structured.outcomeRemarks ?? structured.notes ?? ""),
    extras: [
      { label: "Facility", value: normalizeString(structured.facilityName ?? "") },
      { label: "Officer", value: normalizeString(structured.officer ?? structured.inspectedBy ?? "") },
      { label: "Consignments", value: consignments ? String(consignments) : null },
    ],
  };
};

const parseCargoCertificate = (structured) => ({
  subjectName: normalizeString(structured.consignmentId ?? structured.id ?? ""),
  subjectLabel: "Cargo certificate",
  status: normalizeString(structured.status ?? ""),
  certificateNumber: normalizeString(structured.certificateNumber ?? structured.id ?? ""),
  remarks: normalizeString(structured.remarks ?? structured.notes ?? ""),
  extras: [
    { label: "Certificate type", value: normalizeString(structured.certificateType ?? "") },
    { label: "Consignment", value: normalizeString(structured.consignmentId ?? "") },
  ],
});

const parseFoodCondemnation = (structured) => ({
  subjectName: normalizeString(structured.premiseName ?? structured.productName ?? structured.id ?? ""),
  subjectLabel: "Inspection",
  status: normalizeString(structured.result ?? structured.status ?? structured.actionTaken ?? ""),
  remarks: normalizeString(structured.remarks ?? structured.notes ?? ""),
  extras: [
    { label: "Licence #", value: normalizeString(structured.licenceNo ?? "") },
    { label: "Product", value: normalizeString(structured.productName ?? structured.typeOfFood ?? "") },
    { label: "Follow-up date", value: structured.followUpDate },
  ],
});

const parsePrimaryScreening = (structured) => ({
  subjectName: normalizeString(structured.travellerName ?? structured.name ?? ""),
  subjectLabel: "Traveller",
  travellerGender: normalizeString(structured.gender ?? ""),
  passportNumber: normalizeString(structured.passportNumber ?? structured.passport ?? ""),
  nationality: normalizeString(structured.country ?? structured.nationality ?? ""),
  status: normalizeString(structured.status ?? structured.outcome ?? ""),
  remarks: normalizeString(structured.notes ?? structured.remarks ?? ""),
  extras: [
    { label: "Entry/Exit", value: normalizeString(structured.entryExit ?? "") },
    { label: "Vessel #", value: normalizeString(structured.vesselNumber ?? "") },
    { label: "Recorded by", value: normalizeString(structured.recordedBy ?? "") },
  ],
});

const parseThdf = (structured) => ({
  subjectName: normalizeString(structured.travellerName ?? ""),
  subjectLabel: "Traveller",
  passportNumber: normalizeString(structured.passportNumber ?? ""),
  nationality: normalizeString(structured.nationality ?? ""),
  status: normalizeString(structured.status ?? ""),
  remarks: normalizeString(structured.remarks ?? structured.notes ?? ""),
  extras: [
    { label: "THDF ID", value: normalizeString(structured.thdfId ?? structured.id ?? "") },
    { label: "Traveller ID", value: normalizeString(structured.travellerId ?? "") },
    { label: "Arrival date", value: structured.arrivalDate },
  ],
});

const typeParsers = {
  "traveller.clearanceCertificate": parseClearanceLike,
  "humanRemains.clearanceCertificate": parseClearanceLike,
  "samples.clearanceCertificate": parseClearanceLike,
  detention_certificate: parseDetentionCertificate,
  "cargo.inspection": parseCargoInspection,
  "cargo.certificate": parseCargoCertificate,
  "environmentalHealth.foodCondemnation": parseFoodCondemnation,
  "traveller.primaryScreening": parsePrimaryScreening,
  "traveller.thdfDeclaration": parseThdf,
  "conveyance.sanitationCertificate": parseClearanceLike,
};

export const parseClearancePayload = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { error: "Empty payload received. Scan a Port Health QR code to continue." };
  }

  let structured;
  try {
    if (trimmed.startsWith(THDF_QR_PREFIX)) {
      const id = trimmed.slice(THDF_QR_PREFIX.length);
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
        return { error: "Invalid declaration reference. Scan the traveller’s original QR code." };
      }
      structured = { type: "traveller.thdfDeclaration", version: 1, id, thdfId: id };
    } else {
      structured = JSON.parse(trimmed);
    }
  } catch (err) {
    return { error: "Payload is not valid JSON. Ensure the QR encodes a structured Port Health object." };
  }

  if (!structured || typeof structured !== "object" || Array.isArray(structured)) {
    return { error: "Payload must be a structured Port Health object." };
  }

  const type = normalizeString(structured.type ?? structured.payloadType ?? structured.documentType ?? "");
  if (!SUPPORTED_QR_TYPES.has(type)) {
    const expected = Array.from(SUPPORTED_QR_TYPES).join(", ");
    return {
      error: `Unsupported QR type (${structured.type ?? "unknown"}). Expected one of: ${expected}.`,
    };
  }

  const identifier = resolveIdentifier(structured);
  if (!identifier) {
    return { error: "Identifier missing from payload. Reject and reissue the document." };
  }

  const issuedBy = normalizeString(
    structured.issuedBy ?? structured.issuingOfficer?.name ?? structured.officer ?? "",
  );
  const issuedById = normalizeString(structured.issuedById ?? structured.issuingOfficer?.id ?? "");
  const issuedByRole = normalizeString(structured.issuedByRole ?? structured.issuingOfficer?.role ?? "");
  const status = normalizeString(structured.status ?? structured.outcome ?? "");
  const issuedDate =
    structured.issuedAt ??
    structured.issuedDate ??
    structured.createdAt ??
    structured.date ??
    structured.recordedAt ??
    "";
  const baseResult = {
    id: identifier,
    certificateNumber: normalizeString(structured.certificateNumber ?? structured.reference ?? ""),
    clearanceId: normalizeString(structured.clearanceId ?? ""),
    payloadType: type,
    status,
    issuedDate,
    issuedBy,
    issuedById,
    issuedByRole,
    remarks: normalizeString(structured.remarks ?? structured.notes ?? structured.comments ?? ""),
    portOfEntry: normalizeString(
      structured.port ?? structured.portOfEntry ?? structured.portName ?? structured.pointOfEntry ?? "",
    ),
    version: parseNumber(structured.version) ?? 1,
    scannedAt: new Date().toISOString(),
  };

  const parser = typeParsers[type];
  const parsed = parser ? parser(structured, type) : null;

  return {
    result: {
      ...baseResult,
      ...(parsed || {}),
      subjectLabel: (parsed && parsed.subjectLabel) || QR_TYPE_META[type]?.subjectLabel || "Subject",
    },
  };
};

export const resolveStatusPresentation = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "cleared" || normalized === "approved") {
    return {
      tone: "success",
      label: status,
      icon: <CheckCircle2 className="h-5 w-5" />,
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      message: "Document is valid and cleared for release.",
    };
  }
  if (normalized === "revoked" || normalized === "void") {
    return {
      tone: "danger",
      label: status,
      icon: <XCircle className="h-5 w-5" />,
      badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
      message: "Document is revoked. Direct the bearer back to the issuing desk.",
    };
  }
  return {
    tone: "warning",
    label: status || "Pending",
    icon: <AlertTriangle className="h-5 w-5" />,
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    message: "Document requires manual review before release.",
  };
};

export default function QRScanner() {
  const [scanState, setScanState] = React.useState({ result: null, error: null, raw: "" });

  const handleScan = React.useCallback((payload) => {
    const raw = (payload ?? "").toString();
    const { result, error } = parseClearancePayload(raw);
    if (error) {
      setScanState({ result: null, error, raw });
      return;
    }
    setScanState({ result, error: null, raw });
  }, []);

  const statusPresentation = React.useMemo(
    () => resolveStatusPresentation(scanState.result?.status),
    [scanState.result?.status],
  );

  const subjectLabel = scanState.result?.subjectLabel || "Subject";

  const detailFields = React.useMemo(() => {
    if (!scanState.result) return [];
    const r = scanState.result;
    const entries = [];
    const subjectValue = r.subjectName || r.travellerName || r.id || "—";
    entries.push({ label: subjectLabel, value: subjectValue });
    if (r.travellerGender) entries.push({ label: "Gender", value: r.travellerGender });
    if (r.passportNumber) entries.push({ label: "Passport/ID", value: r.passportNumber });
    if (r.nationality) entries.push({ label: "Nationality", value: r.nationality });
    if (r.certificateNumber) entries.push({ label: "Certificate #", value: r.certificateNumber });
    if (r.clearanceId || r.id) entries.push({ label: "Record ID", value: r.clearanceId || r.id });
    if (r.portOfEntry) entries.push({ label: "Port of entry", value: r.portOfEntry });
    if (r.issuedDate) entries.push({ label: "Issued", value: formatDateTime(r.issuedDate) });
    if (r.issuedBy) entries.push({ label: "Issuing officer", value: r.issuedBy });
    if (r.issuedByRole) entries.push({ label: "Officer role", value: r.issuedByRole });
    if (r.issuedById) entries.push({ label: "Officer ID", value: r.issuedById });
    (r.extras || []).forEach((entry) => {
      if (!entry || !entry.label) return;
      const value = entry.value ?? "";
      entries.push({ label: entry.label, value: value || "—" });
    });
    return entries;
  }, [scanState.result, subjectLabel]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Port Health QR Scanner</h1>
      <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="rounded-2xl bg-white p-5 shadow border border-slate-200">
            <h2 className="text-base font-semibold text-slate-700">Scan QR</h2>
            <p className="mt-1 text-sm text-slate-500">
              Use the device camera or paste the QR payload to verify Port Health documents (clearance certificates, detention certificates, cargo QR, THDF, etc.).
            </p>
            <div className="mt-4">
              <DocumentScanner
                onScan={handleScan}
                documentTypeLabel="Port Health QR"
                hints={CLEARANCE_HINTS}
                startButtonLabel="Start scan"
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700">Raw payload</h3>
            {scanState.raw ? (
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-900/5 p-3 text-xs text-slate-700">
                {scanState.raw}
              </pre>
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                Raw QR contents will appear here after a scan.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow border border-slate-200">
          <h2 className="text-base font-semibold text-slate-700">Verification result</h2>
          {!scanState.result && !scanState.error && (
            <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
              Scan a Port Health QR to view the document summary and decision cues.
            </div>
          )}

          {scanState.error && (
            <div className="mt-4 space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
              <div className="flex items-center gap-2 font-semibold">
                <XCircle className="h-5 w-5" />
                Verification failed
              </div>
              <p>{scanState.error}</p>
              <div className="rounded-lg bg-white/70 p-3 text-xs text-rose-600">
                <p className="font-semibold text-rose-700">Troubleshooting tips</p>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>Confirm the QR is a Port Health document and not a lab/vaccination QR.</li>
                  <li>Check that the issuing officer reprinted the document after the latest update.</li>
                  <li>Re-scan using a brighter light source to avoid truncated payloads.</li>
                </ul>
              </div>
            </div>
          )}

          {scanState.result?.payloadType === "traveller.thdfDeclaration" && (
            <ThdfQrLookup key={scanState.result.id} id={scanState.result.id} />
          )}

          {scanState.result && scanState.result.payloadType !== "traveller.thdfDeclaration" && (
            <div className="mt-4 space-y-5">
              <div
                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold ${statusPresentation.badgeClass}`}
              >
                <div className="flex items-center gap-2">
                  {statusPresentation.icon}
                  <span>{statusPresentation.label}</span>
                </div>
                <span className="text-xs font-medium">Version v{scanState.result.version}</span>
              </div>
              <p className="text-sm text-slate-600">{statusPresentation.message}</p>
              <dl className="grid grid-cols-1 gap-4 text-sm text-slate-700 md:grid-cols-2">
                {detailFields.map((field) => (
                  <div key={`${field.label}-${field.value}`}>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {field.label}
                    </dt>
                    <dd className="mt-1 text-slate-800">{field.value || "—"}</dd>
                  </div>
                ))}
              </dl>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <Info className="h-4 w-4" />
                  Notes
                </div>
                <p className="mt-2 text-slate-600">{scanState.result.remarks || "No additional remarks captured."}</p>
              </div>
              <p className="text-xs text-slate-400">
                Last scanned {formatDateTime(scanState.result.scannedAt)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
